// [S-320c] Tenant Classification — tenant_admin admin surface, distinct
// from the platform_admin /classification page. A tenant inherits the
// platform catalog by default; it can re-seed an isolated, editable copy
// into its own namespace, customize it, and revert to inheriting — with
// zero effect on the platform catalog or any other organization.
//
// Wired to the S-320b routes via the organization-scoped data hooks. The tabbed
// CRUD is the shared <ClassificationCatalog scope="tenant">; this screen
// adds the Inherited/Customized mode header and the re-seed / revert
// lifecycle actions, and renders the catalog read-only while inherited.
import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  LoadingState,
  PageHead,
  Pill,
} from "../components";
import { ClassificationCatalog } from "./ClassificationCatalog";
import {
  useReseedTenantClassification,
  useRevertTenantClassification,
  useTenantClassificationMode,
  type TenantClassificationModeWire,
} from "../data/classificationQuery";

export function TenantClassification(): JSX.Element {
  const mode = useTenantClassificationMode();
  const reseed = useReseedTenantClassification();
  const revert = useRevertTenantClassification();
  const [confirming, setConfirming] = useState<null | "reseed" | "revert">(
    null
  );

  const forked = mode.data?.forked ?? false;
  const busy = reseed.isPending || revert.isPending;

  return (
    <>
      <PageHead
        eyebrow="Organization · classification"
        title={
          <>
            Your studio's <em>vocabulary.</em>
          </>
        }
        sub="Inherit the platform catalog, or make your own editable copy to customize categories, contract types, CBAs, and facets for your studio. Your changes never touch the platform catalog or any other organization."
      />

      {mode.isLoading && <LoadingState label="Loading classification mode…" />}
      {mode.isError && (
        <Alert
          label="Couldn't load classification mode"
          tone="error"
          data-testid="tcls-error"
        >
          {mode.error?.message ?? "unknown error"}
        </Alert>
      )}

      {mode.data !== undefined && (
        <>
          <ModeHeader
            mode={mode.data}
            busy={busy}
            onReseed={() => setConfirming("reseed")}
            onRevert={() => setConfirming("revert")}
          />
          <ClassificationCatalog scope="tenant" readOnly={!forked} />
        </>
      )}

      <ReseedConfirmDialog
        open={confirming === "reseed"}
        forked={forked}
        pending={reseed.isPending}
        error={reseed.error}
        onConfirm={() =>
          reseed.mutate(undefined, { onSuccess: () => setConfirming(null) })
        }
        onClose={() => {
          reseed.reset();
          setConfirming(null);
        }}
      />
      <RevertConfirmDialog
        open={confirming === "revert"}
        pending={revert.isPending}
        error={revert.error}
        onConfirm={() =>
          revert.mutate(undefined, { onSuccess: () => setConfirming(null) })
        }
        onClose={() => {
          revert.reset();
          setConfirming(null);
        }}
      />
    </>
  );
}

function ModeHeader({
  mode,
  busy,
  onReseed,
  onRevert,
}: {
  mode: TenantClassificationModeWire;
  busy: boolean;
  onReseed: () => void;
  onRevert: () => void;
}): JSX.Element {
  if (!mode.forked) {
    return (
      <Alert
        label="Inheriting the platform catalog"
        spacedBottom
        cta={
          <Button
            variant="primary"
            className="btn--s"
            onClick={onReseed}
            disabled={busy}
            data-testid="tcls-reseed"
          >
            Re-seed from platform
          </Button>
        }
      >
        <span data-testid="tcls-mode-inherited">
          Your studio reads the shared platform vocabulary. Re-seed to make an
          editable copy — these entries stay read-only until you do.
        </span>
      </Alert>
    );
  }
  return (
    <div
      className="cls__panel__head"
      data-testid="tcls-mode-customized"
      style={{ marginBottom: 16 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Pill variant="high">Customized</Pill>
        <span className="cls__panel__head__sub">
          {mode.counts} {mode.counts === 1 ? "entry" : "entries"}
          {mode.seededAt !== null ? <> · seeded {formatDate(mode.seededAt)}</> : null}
        </span>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          variant="ghost"
          className="btn--s"
          onClick={onReseed}
          disabled={busy}
          data-testid="tcls-reseed"
        >
          Re-seed from platform
        </Button>
        <Button
          variant="ghost"
          className="btn--s"
          onClick={onRevert}
          disabled={busy}
          data-testid="tcls-revert"
        >
          Revert to inherited
        </Button>
      </div>
    </div>
  );
}

function ReseedConfirmDialog({
  open,
  forked,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  forked: boolean;
  pending: boolean;
  error: Error | null;
  onConfirm: () => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Re-seed from the platform catalog"
      description={
        forked
          ? "This replaces your studio's customized catalog with a fresh copy of the current platform catalog. Your edits will be lost."
          : "This forks an editable copy of the current platform catalog into your studio. You can customize it, or revert to inheriting at any time."
      }
    >
      {error !== null && (
        <div className="cls__error" role="alert">
          Could not re-seed: {error.message}
        </div>
      )}
      <div className="cls__modal__ft">
        <Button variant="ghost" type="button" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={onConfirm}
          disabled={pending}
          data-testid="tcls-reseed-confirm"
        >
          {pending
            ? "Re-seeding…"
            : forked
              ? "Replace my catalog"
              : "Make an editable copy"}
        </Button>
      </div>
    </Dialog>
  );
}

function RevertConfirmDialog({
  open,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean;
  pending: boolean;
  error: Error | null;
  onConfirm: () => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Revert to the inherited catalog"
      description="This discards your studio's customized catalog and goes back to inheriting the platform vocabulary. To customize again you'd re-seed and redo your edits."
    >
      {error !== null && (
        <div className="cls__error" role="alert">
          Could not revert: {error.message}
        </div>
      )}
      <div className="cls__modal__ft">
        <Button variant="ghost" type="button" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={onConfirm}
          disabled={pending}
          data-testid="tcls-revert-confirm"
        >
          {pending ? "Reverting…" : "Revert to inherited"}
        </Button>
      </div>
    </Dialog>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
